import axios from 'axios';
import { captureException, withScope } from '@sentry/react';
import camelcaseKeys from 'camelcase-keys';
import { refreshTokens } from '@api';
import { getApiErrorCode } from '@utils';
import { useApplicationStore } from '@data';
import { getAccessToken } from '../utils';

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_BASE_API_URL,
});

apiClient.interceptors.response.use(
  (response) => {
    if (response.data) {
      response.data = camelcaseKeys(response.data, { deep: true });
    }
    return response;
  },
  async (error) => {
    if (error.response?.data) {
      error.response.data = camelcaseKeys(error.response.data, { deep: true });
    }

    // Retries request if 401 error received
    if (error.response?.status === 401) {
      const {
        auth: { accessToken, setAccessToken },
      } = useApplicationStore.getState();

      if (accessToken) {
        try {
          const { accessToken: newAccessToken } = await refreshTokens({
            accessToken,
          });

          setAccessToken(newAccessToken);

          // Retries original request with new token
          error.config.headers.Authorization = `Bearer ${newAccessToken}`;
          return await apiClient(error.config);
        } catch (err) {
          return Promise.reject(err);
        }
      }
    }

    withScope((scope) => {
      if (error.response?.data?.errorCode) {
        error.name = getApiErrorCode(error);
      }
      scope.setContext('response', {
        status: error.response?.status,
      });
      captureException(error);
    });

    return Promise.reject(error);
  },
);

apiClient.interceptors.request.use((config) => {
  if (
    config.data &&
    !(config.data instanceof FormData) &&
    config.url !== '/api/v1/media'
  ) {
    config.data = camelcaseKeys(config.data, { deep: true });
  }

  const accessToken = getAccessToken();
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }

  return config;
});

export default apiClient;
